import { MontoCRC, MontoUSD } from '@/components/dominio';
import { BankAccountPickerBC } from '@/components/filtros/BankAccountPickerBC';
import { MultiSelect } from '@/components/filtros/MultiSelect';
import { DisponibilidadCuentas } from '@/components/propuestas-pago/DisponibilidadCuentas';
import { SelectorFacturasPorProveedor } from '@/components/propuestas-pago/SelectorFacturasPorProveedor';
import { TotalesPropuestaBar } from '@/components/propuestas-pago/TotalesPropuestaBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import { useCrearPropuestaPago } from '@/hooks/usePropuestasPago';
import { useVencimientosDetalle } from '@/hooks/useVencimientosDetalle';
import { usePropuestaEnConstruccion } from '@/lib/PropuestaEnConstruccionContext';
import { lineaDesdeFactura } from '@/lib/montosPropuesta';
import { resumenSaldoCuentas } from '@/lib/resumenCuentas';
import {
  type SaldoCuentaPropuesta,
  TERMINO_PAGO_CONTADO,
  type TipoPropuestaPago,
} from '@adelante/shared';
import { ArrowLeft, CircleNotch, MagicWand } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function PropuestaPagoNuevaPantalla() {
  const { data, isLoading } = useVencimientosDetalle();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tipo: TipoPropuestaPago = params.get('tipo') === 'contado' ? 'CONTADO' : 'CREDITO';
  const canasta = usePropuestaEnConstruccion();
  const crearPropuesta = useCrearPropuestaPago();
  const { data: cuentasBancoData } = useCuentasBancoBC();

  const [terminosFiltro, setTerminosFiltro] = useState<string[]>([]);
  const [monedaFiltro, setMonedaFiltro] = useState<string[]>([]);
  const [proveedoresFiltro, setProveedoresFiltro] = useState<string[]>([]);
  const [fechaCorte, setFechaCorte] = useState('');
  const [saldosCuentas, setSaldosCuentas] = useState<SaldoCuentaPropuesta[]>([]);
  const [cuentaParaTodos, setCuentaParaTodos] = useState<string | null>(null);

  const [dialogAbierto, setDialogAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [fechaPagoPropuesta, setFechaPagoPropuesta] = useState('');

  const detalle = data?.data ?? [];

  const opcionesTerminos = useMemo(() => {
    const set = new Set<string>();
    for (const f of detalle) if (f.paymentTermsCode) set.add(f.paymentTermsCode);
    return [...set].sort().map((t) => ({ value: t, label: t }));
  }, [detalle]);

  const opcionesMoneda = useMemo(() => {
    const set = new Set<string>();
    for (const f of detalle) set.add(f.currencyCode || 'CRC');
    return [...set].sort().map((m) => ({ value: m, label: m }));
  }, [detalle]);

  const opcionesProveedores = useMemo(() => {
    const porCodigo = new Map<string, string>();
    for (const f of detalle) {
      if (!porCodigo.has(f.vendorCode)) porCodigo.set(f.vendorCode, f.vendorName ?? f.vendorCode);
    }
    return [...porCodigo.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, nombreProveedor]) => ({ value, label: nombreProveedor }));
  }, [detalle]);

  const facturasCandidatas = useMemo(() => {
    return detalle.filter((f) => {
      // Leasing, alquileres y créditos negociados no se pagan desde las propuestas.
      if (f.pactado) return false;
      // Sin términos elegidos a mano, el tipo decide: contado = solo CONTADO; crédito = todo lo demás
      // (incluye proveedores sin términos).
      const terminos = f.paymentTermsCode ?? '';
      if (terminosFiltro.length > 0) {
        if (!terminosFiltro.includes(terminos)) return false;
      } else if ((terminos === TERMINO_PAGO_CONTADO) !== (tipo === 'CONTADO')) {
        return false;
      }
      if (monedaFiltro.length > 0 && !monedaFiltro.includes(f.currencyCode || 'CRC')) {
        return false;
      }
      if (proveedoresFiltro.length > 0 && !proveedoresFiltro.includes(f.vendorCode)) {
        return false;
      }
      if (!fechaCorte || !f.dueDate || f.dueDate > fechaCorte) return false;
      return true;
    });
  }, [detalle, tipo, terminosFiltro, monedaFiltro, proveedoresFiltro, fechaCorte]);

  const vendorCodesCandidatos = useMemo(
    () => new Set(facturasCandidatas.map((f) => f.vendorCode)),
    [facturasCandidatas],
  );

  const resumenCuentas = useMemo(
    () => resumenSaldoCuentas(canasta.lineas, saldosCuentas),
    [canasta.lineas, saldosCuentas],
  );

  // Líneas que siguen seleccionadas (se guardan en el navegador) pero de proveedores que este criterio
  // no muestra: no se ven en la tabla, pero SÍ suman en los totales.
  const lineasOcultas = useMemo(
    () => canasta.lineas.filter((l) => !vendorCodesCandidatos.has(l.vendorCode)),
    [canasta.lineas, vendorCodesCandidatos],
  );
  const totalOculto = useMemo(() => {
    const t = new Map<string, number>();
    for (const l of lineasOcultas) {
      const m = l.currencyCode || 'CRC';
      t.set(m, (t.get(m) ?? 0) + l.montoAPagar);
    }
    return [...t.entries()];
  }, [lineasOcultas]);

  function aplicarCriterio() {
    if (canasta.lineas.length > 0) {
      const continuar = confirm(
        'Ya hay facturas seleccionadas de una corrida anterior. ¿Reemplazarlas con el resultado de este criterio?',
      );
      if (!continuar) return;
    }
    canasta.reemplazar(facturasCandidatas.map(lineaDesdeFactura));
  }

  function aplicarCuentaATodos() {
    if (!cuentaParaTodos) return;
    const cuenta = cuentasBancoData?.data?.find((c) => c.number === cuentaParaTodos);
    const monedaCuenta = cuenta?.currencyCode || 'CRC';
    let saltadas = 0;
    canasta.reemplazar(
      canasta.lineas.map((l) => {
        if ((l.currencyCode || 'CRC') !== monedaCuenta) {
          saltadas++;
          return l;
        }
        return { ...l, cuentaBancoOrigen: cuentaParaTodos };
      }),
    );
    if (saltadas > 0) {
      toast.warning(
        `${saltadas} factura(s) en otra moneda no se asignaron a esa cuenta (${monedaCuenta}).`,
      );
    }
  }

  function crear() {
    if (!nombre.trim() || canasta.lineas.length === 0) return;
    crearPropuesta.mutate(
      {
        nombre: nombre.trim(),
        tipo,
        fechaPagoPropuesta: fechaPagoPropuesta || null,
        lineas: canasta.lineas.map((l) => ({
          entryNo: l.entryNo,
          vendorCode: l.vendorCode,
          montoAPagar: l.montoAPagar,
          montoAPagarLCY: l.montoAPagarLCY,
          currencyCode: l.currencyCode,
          cuentaBancoOrigen: l.cuentaBancoOrigen,
        })),
        saldosCuentas,
      },
      {
        onSuccess: (r) => {
          toast.success('Propuesta creada');
          canasta.limpiar();
          setDialogAbierto(false);
          if (r.data) navigate(`/vencimientos/propuestas/${r.data.id}`);
        },
        onError: () => toast.error('Error al crear la propuesta'),
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            navigate(`/vencimientos/propuestas${tipo === 'CONTADO' ? '?tipo=contado' : ''}`)
          }
        >
          <ArrowLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Nueva propuesta de pago de {tipo === 'CONTADO' ? 'contado' : 'crédito'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Elegí un criterio (términos de pago + fecha de corte, igual que en BC) y generá la
            propuesta de una sola vez.
          </p>
        </div>
      </div>

      <TotalesPropuestaBar totales={canasta.totalPorMoneda} resumenCuentas={resumenCuentas} />

      <Card>
        <CardHeader>
          <CardTitle>Criterio</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <MultiSelect
            name="terminos-nueva"
            placeholder={
              tipo === 'CONTADO'
                ? 'Términos de pago (CONTADO)'
                : 'Términos de pago (todos menos CONTADO)'
            }
            opciones={opcionesTerminos}
            seleccionados={terminosFiltro}
            onChange={setTerminosFiltro}
            className="w-[200px] justify-between font-normal"
          />
          <MultiSelect
            name="moneda-nueva"
            placeholder="Moneda (todas)"
            opciones={opcionesMoneda}
            seleccionados={monedaFiltro}
            onChange={setMonedaFiltro}
            className="w-[160px] justify-between font-normal"
          />
          <MultiSelect
            name="proveedores-nueva"
            placeholder="Proveedores (todos)"
            opciones={opcionesProveedores}
            seleccionados={proveedoresFiltro}
            onChange={setProveedoresFiltro}
            todosPorDefecto
            conBusqueda
            className="w-[220px] justify-between font-normal"
          />
          <div className="space-y-1">
            <Label htmlFor="fecha-corte" className="text-xs text-muted-foreground">
              Fecha de corte
            </Label>
            <Input
              id="fecha-corte"
              type="date"
              value={fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <Button onClick={aplicarCriterio} disabled={!fechaCorte || isLoading}>
            {isLoading ? (
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            ) : (
              <MagicWand className="h-4 w-4" weight="bold" aria-hidden="true" />
            )}
            Aplicar criterio
          </Button>
          {fechaCorte && (
            <p className="w-full text-xs text-muted-foreground">
              {facturasCandidatas.length} factura(s) cumplen este criterio. "Aplicar criterio" solo
              arma la selección abajo — no guarda nada todavía. Para guardar la propuesta, usá
              "Crear propuesta" al final.
            </p>
          )}
        </CardContent>
      </Card>

      {lineasOcultas.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span>
            Hay <strong>{lineasOcultas.length} factura(s)</strong> seleccionadas de otros
            proveedores que este criterio no muestra (
            {totalOculto.map(([moneda, total], i) => (
              <span key={moneda}>
                {i > 0 && ' · '}
                {moneda === 'USD' ? <MontoUSD monto={total} /> : <MontoCRC monto={total} />}
              </span>
            ))}
            ). Siguen sumando en los totales de arriba.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            onClick={() =>
              canasta.reemplazar(
                canasta.lineas.filter((l) => vendorCodesCandidatos.has(l.vendorCode)),
              )
            }
          >
            Quitar de la selección
          </Button>
        </div>
      )}

      {canasta.lineas.length > 0 && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-end justify-between gap-4 space-y-0">
              <CardTitle>Revisar y ajustar</CardTitle>
              <div className="flex items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Aplicar cuenta bancaria a todos los proveedores
                  </Label>
                  <BankAccountPickerBC value={cuentaParaTodos} onChange={setCuentaParaTodos} />
                </div>
                <Button type="button" variant="outline" onClick={aplicarCuentaATodos}>
                  Aplicar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <SelectorFacturasPorProveedor
                facturas={detalle}
                vendorCodesVisibles={vendorCodesCandidatos}
                value={canasta.lineas}
                onChange={canasta.reemplazar}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Disponibilidad de cuentas</CardTitle>
            </CardHeader>
            <CardContent>
              <DisponibilidadCuentas
                lineas={canasta.lineas}
                saldos={saldosCuentas}
                onChange={setSaldosCuentas}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/30 p-4">
            <div className="flex gap-4 text-sm">
              {canasta.totalPorMoneda.map((t) => (
                <span key={t.currencyCode}>
                  Pagos {t.currencyCode}:{' '}
                  {t.currencyCode === 'USD' ? (
                    <MontoUSD monto={t.total} className="font-semibold" />
                  ) : (
                    <MontoCRC monto={t.total} className="font-semibold" />
                  )}
                </span>
              ))}
            </div>
            <Button
              onClick={() => {
                setFechaPagoPropuesta((actual) => actual || fechaCorte);
                setDialogAbierto(true);
              }}
            >
              Crear propuesta
            </Button>
          </div>
        </>
      )}

      <Dialog open={dialogAbierto} onOpenChange={setDialogAbierto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva propuesta de pago</DialogTitle>
            <DialogDescription>
              {canasta.lineas.length} factura(s) seleccionada(s). Todavía no se escribe nada en
              Business Central.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="nombre-propuesta">Nombre</Label>
              <Input
                id="nombre-propuesta"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej. Pagos semana 15-19 sep"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fecha-propuesta">Fecha de pago propuesta</Label>
              <Input
                id="fecha-propuesta"
                type="date"
                value={fechaPagoPropuesta}
                onChange={(e) => setFechaPagoPropuesta(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Se prellenó con la fecha de corte del criterio — cambiala si el pago sale otro día.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogAbierto(false)}>
              Cancelar
            </Button>
            <Button disabled={!nombre.trim() || crearPropuesta.isPending} onClick={crear}>
              {crearPropuesta.isPending ? 'Creando...' : 'Crear propuesta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
